const SAVE_PREFIX = 'voxelgame_world_';
const LEGACY_SAVE_KEY = 'voxelgame_save';
const MAX_WORLDS = 5;

export class SaveManager {
  constructor(slot = 0) {
    this.slot = slot;
    this.worldName = `Mundo ${slot + 1}`;
    this.modifications = new Map();
    this._saveTimer = 0;
    this._saveInterval = 30;
    this._getPlayerData = null;
    this._getInventoryData = null;
    this._lastSaveFailed = false;
    this.onSaveError = null;
  }

  get _saveKey() {
    return SAVE_PREFIX + this.slot;
  }

  get _tileEntityKey() {
    return `voxelgame_te_${this.slot}`;
  }

  get _achievementsKey() {
    return `voxelgame_ach_${this.slot}`;
  }

  recordBlockChange(wx, wy, wz, blockId) {
    this.modifications.set(`${wx},${wy},${wz}`, blockId);
  }

  update(dt) {
    this._saveTimer -= dt;
    if (this._saveTimer <= 0) {
      this._saveTimer = this._saveInterval;
      const p = this._getPlayerData ? this._getPlayerData() : null;
      const i = this._getInventoryData ? this._getInventoryData() : null;
      this.save(p, i);
    }
  }

  save(playerData, inventoryData) {
    const data = {
      version: 1,
      slot: this.slot,
      worldName: this.worldName || `Mundo ${this.slot + 1}`,
      timestamp: Date.now(),
      player: playerData || null,
      blocks: Object.fromEntries(this.modifications),
      inventory: inventoryData || null,
    };
    try {
      localStorage.setItem(this._saveKey, JSON.stringify(data));
      if (this._lastSaveFailed) {
        this._lastSaveFailed = false;
      }
      if (this.onSaveSuccess) {
        this.onSaveSuccess();
      }
    } catch (e) {
      this._lastSaveFailed = true;
      console.error('[SaveManager] Error al guardar:', e.name, e.message);
      if (this.onSaveError) {
        this.onSaveError('Error al guardar: almacenamiento lleno');
      }
    }
  }

  load() {
    try {
      const raw = localStorage.getItem(this._saveKey);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (data && typeof data === 'object') {
        if (data.blocks && typeof data.blocks !== 'object') data.blocks = {};
        if (data.player && typeof data.player !== 'object') data.player = null;
        if (data.inventory && typeof data.inventory !== 'object') data.inventory = null;
        return data;
      }
      return null;
    } catch (e) {
      console.error('[SaveManager] Error al cargar save:', e.message);
      return null;
    }
  }

  applyToChunk(chunk, chunkSize) {
    const wx0 = chunk.cx * chunkSize;
    const wy0 = chunk.cy * chunkSize;
    const wz0 = chunk.cz * chunkSize;

    for (const [key, blockId] of this.modifications) {
      const [wx, wy, wz] = key.split(',').map(Number);
      const lx = wx - wx0;
      const ly = wy - wy0;
      const lz = wz - wz0;
      if (lx >= 0 && lx < chunkSize && ly >= 0 && ly < chunkSize && lz >= 0 && lz < chunkSize) {
        chunk.setBlock(lx, ly, lz, blockId);
      }
    }
  }

  loadModifications() {
    const data = this.load();
    if (data && data.blocks) {
      for (const [key, blockId] of Object.entries(data.blocks)) {
        this.modifications.set(key, blockId);
      }
    }
    return data;
  }

  clearSave() {
    localStorage.removeItem(this._saveKey);
    localStorage.removeItem(this._tileEntityKey);
    localStorage.removeItem(this._achievementsKey);
    this.modifications.clear();
  }

  // ============================================================
  // Static methods for world list management
  // ============================================================

  static migrateIfNeeded() {
    // One-time migration: move legacy save to slot 0
    const legacy = localStorage.getItem(LEGACY_SAVE_KEY);
    if (legacy && !localStorage.getItem(SAVE_PREFIX + '0')) {
      localStorage.setItem(SAVE_PREFIX + '0', legacy);
      // Migrate tile entities
      const legacyTE = localStorage.getItem('voxelgame_tileentities');
      if (legacyTE) localStorage.setItem('voxelgame_te_0', legacyTE);
      // Migrate achievements
      const legacyAch = localStorage.getItem('voxelgame_achievements');
      if (legacyAch) localStorage.setItem('voxelgame_ach_0', legacyAch);
      // Remove legacy keys
      localStorage.removeItem(LEGACY_SAVE_KEY);
      localStorage.removeItem('voxelgame_tileentities');
      localStorage.removeItem('voxelgame_achievements');
    }
  }

  static getWorldList() {
    const worlds = [];
    for (let i = 0; i < MAX_WORLDS; i++) {
      const raw = localStorage.getItem(SAVE_PREFIX + i);
      if (raw) {
        try {
          const data = JSON.parse(raw);
          worlds.push({
            slot: i,
            name: data.worldName || `Mundo ${i + 1}`,
            mode: data.player?.gameMode || 'survival',
            difficulty: data.player?.difficulty || 'normal',
            seed: data.player?.seed || '',
            lastPlayed: data.timestamp || 0,
            occupied: true,
          });
        } catch {
          worlds.push({ slot: i, name: `Mundo ${i + 1}`, occupied: true, lastPlayed: 0 });
        }
      } else {
        worlds.push({ slot: i, name: '', occupied: false, lastPlayed: 0 });
      }
    }
    return worlds;
  }

  static deleteWorld(slot) {
    localStorage.removeItem(SAVE_PREFIX + slot);
    localStorage.removeItem(`voxelgame_te_${slot}`);
    localStorage.removeItem(`voxelgame_ach_${slot}`);
  }

  static getNextFreeSlot() {
    for (let i = 0; i < MAX_WORLDS; i++) {
      if (!localStorage.getItem(SAVE_PREFIX + i)) return i;
    }
    return -1; // all slots full
  }

  static hasAnySave() {
    for (let i = 0; i < MAX_WORLDS; i++) {
      if (localStorage.getItem(SAVE_PREFIX + i)) return true;
    }
    return false;
  }
}
